import * as React from 'react';
import { useEventProxy } from 'src/hooks/useEventProxy';
import { PostMessageEvents } from 'src/types/Events';

interface IPostMessageContext {
  postMessage: <T extends keyof PostMessageEvents>(type: T, data: object) => void;
  source: string;

  // Methods are postMessage calls from the parent frame
  methodProxy: EventTarget;
  // Events are messages sent back to the parent frame
  eventProxy: EventTarget;
}

export const PostMessageContext = React.createContext<IPostMessageContext | null>(null);

interface IPostMessageProviderProps {
  children: React.ReactNode,
  source: string;
}

interface Message {
  activeProductUniqueId: string | null;
  data: PostMessageEvents[keyof PostMessageEvents];
  source: string;
  type: keyof PostMessageEvents;
}

const makeCustomEventFromMessageEvent = (e: MessageEvent) => {
  const { type, data, origin } = e.data;
  const detail = { ...data, origin };
  return new CustomEvent(type, { detail });
};

// IMPORTANT: be ready to capture postMessage events right away, even before the tree is rendered.
// The entry scripts post an init message as soon as this script's onload event fires.
const capturedEventQueue: CustomEvent[] = [];
const captureMessageEvent = (e: MessageEvent) => {
  capturedEventQueue.push(makeCustomEventFromMessageEvent(e));
};
window.addEventListener('message', captureMessageEvent);

export const PostMessageProvider = (props: IPostMessageProviderProps) => {
  // NOTE: throwing this here so it's caught by error boundary
  if (!window.parent) {
    throw new Error('Misconfigured. Expected to run inside iframe');
  }

  const { children, source } = props;
  const [origin, setOrigin] = React.useState<string | null>(null);
  const [activeProductUniqueId, setActiveProductUniqueId] = React.useState<string | null>(null);
  const messageQueue = React.useRef<Message[]>([]);

  // Proxy methods and events so that descendant components don't have to work with postMessage directly
  const eventProxy = useEventProxy();
  const methodProxy = useEventProxy();

  // Helper function to proxy a method call internally
  const forwardMethodCall = React.useCallback((e: CustomEvent) => {
    // Make sure it came from us (it can also come from Webpack, the Chrome extension, etc.)
    if (!e.type || !e.detail) {
      return;
    }
    if (!origin && e.detail.origin) {
      setOrigin(e.detail.origin);
    }
    // TODO: also get this from `setActiveProduct`?
    if (!activeProductUniqueId && e.detail.variantId) {
      setActiveProductUniqueId(e.detail.variantId);
    }
    if (!activeProductUniqueId && typeof e.detail.activeProduct === 'string') {
      setActiveProductUniqueId(e.detail.activeProduct);
    }
    if (e.type === 'setActiveProduct' && e.detail.sku) {
      setActiveProductUniqueId(e.detail.sku);
    }
    methodProxy.dispatchEvent(e);
    // console.log('CLYDE: proxy method', e);
  }, [origin, setOrigin, methodProxy]);

  // Proxy any messages that were captured before we were ready
  React.useEffect(() => {
    while (capturedEventQueue.length) {
      const e = capturedEventQueue.shift();
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      forwardMethodCall(e!);
    }
    window.removeEventListener('message', captureMessageEvent);
  }, [forwardMethodCall]);

  // Repeat any postMessage calls before origin was known
  React.useEffect(() => {
    if (origin) {
      while (messageQueue.current.length) {
        const message = messageQueue.current.shift();
        window.parent.postMessage(message, origin);
      }
    }
  }, [origin]);

  // Handle new messages from parent frame
  React.useEffect(() => {
    const handler = (e: MessageEvent) => {
      const ce = makeCustomEventFromMessageEvent(e);
      forwardMethodCall(ce);
    };
    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
    };
  }, [forwardMethodCall]);

  // Interface for child components to tallback to the parent frame
  const postMessage = React.useCallback(<T extends keyof PostMessageEvents>(type: T, data: object) => {
    const message: Message = {
      activeProductUniqueId,
      data,
      source,
      type,
    };

    // Forward message to any event subscribers
    // TODO: eventually we'll need another layer here. Instead of being prescriptive about
    // "how" to notify the parent frame, we need to declare what happened.
    const ev = new CustomEvent(type, {
      detail: message,
    });
    eventProxy.dispatchEvent(ev);
    // console.log('CLYDE: proxy event', ev);

    if (origin) {
      window.parent.postMessage(message, origin);
    } else {
      messageQueue.current.push(message);
    }
  }, [origin, source, activeProductUniqueId]);

  const value: IPostMessageContext = {
    postMessage,
    source,
    methodProxy,
    eventProxy,
  };

  return (
    <PostMessageContext.Provider value={value}>
      {children}
    </PostMessageContext.Provider>
  );
};

export const usePostMessageContext = () => {
  const context = React.useContext(PostMessageContext);
  if (!context) {
    throw new Error('Context misconfigured: PostMessageContext');
  }
  return context;
};

export const usePostMessageHandler = (type: string, handler: (e: CustomEvent) => void) => {
  const { methodProxy } = usePostMessageContext();
  React.useEffect(() => {
    methodProxy.addEventListener(type, handler as EventListener);
    return () => {
      methodProxy.removeEventListener(type, handler as EventListener);
    };
  }, [addEventListener, removeEventListener, type, handler]);
};
